import { Injectable } from '@angular/core';
import { from, Observable, of, Subject, zip } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take, takeUntil, tap, toArray } from 'rxjs/operators';
import { AvanoLoadingIndicatorService } from '../..';
import { AvanoUnsavedChangesSupport } from './avano-unsaved-changes.common';

type SaveChangesButtons = 'discard' | 'cancel' | 'save'
interface DialogResponse {
  context: AvanoUnsavedChangesSupport;
  applyAllChecked?: boolean;
  buttonClicked: SaveChangesButtons
}
export const GenericUnsavedChangesContext: AvanoUnsavedChangesSupport = {
  unsavedChangesConfiguration: {
    componentIdentifier: 'no-context-unsaved-changes',
    isDirty: () => true
  }
}

@Injectable({
  providedIn: 'root'
})
export class AvanoUnsavedChangesService {
  private registry: Map<string, AvanoUnsavedChangesSupport> = new Map();
  private _allUnsavedComponents$: Subject<AvanoUnsavedChangesSupport[]> = new Subject();
  readonly allUnsavedComponents$ = this._allUnsavedComponents$.asObservable();
  private _dialogResponses$ = new Subject<DialogResponse>();
  

  register(component: AvanoUnsavedChangesSupport) {
    const componentId = component.unsavedChangesConfiguration?.componentIdentifier;
    if (!componentId) {
      console.error("Component has no valid configuration or id");
      return;
    }
    if (this.registry.has(componentId)) {
      console.error("Component with same Identifier already registered", componentId);
    } else {
      this.registry.set(componentId, component);
    }
  }

  unregister(component: AvanoUnsavedChangesSupport) {
    const componentId = component.unsavedChangesConfiguration?.componentIdentifier;
    if (this.registry.has(componentId)) {
      this.registry.delete(componentId);
    }
  }

  clearRegistry() {
    this.registry.clear()
  }

  getDirtyComponents(): AvanoUnsavedChangesSupport[] {
    const componentsIterator = this.registry.values();
    let next = componentsIterator.next();
    const dirtyComponents: AvanoUnsavedChangesSupport[] = [];
    while(next.done !== true) {
      const isDirty = typeof next.value.unsavedChangesConfiguration?.isDirty == 'function' && next.value.unsavedChangesConfiguration.isDirty();
      if (isDirty) dirtyComponents.push(next.value);
      next = componentsIterator.next();
    }
    return dirtyComponents;
  }

  promptDirtyComponentsActions(components: AvanoUnsavedChangesSupport[]): Observable<boolean> {
    this._allUnsavedComponents$.next(components);
    let cancelRequested = false;
    const finished$ = new Subject<void>();
    return from(components).pipe(
      concatMap((component, currentIndex) => {
        if (cancelRequested) return of(false);
        return this._dialogResponses$.pipe(
          filter(response => {
            const match = response.context === component;
            return match;
          }),
          switchMap(response => {
            const { buttonClicked, applyAllChecked } = response;
            switch(buttonClicked) {
              case 'cancel': 
                cancelRequested = true;
                this._allUnsavedComponents$.next([]);
                return of(false);
              case 'discard':
                if (applyAllChecked) {
                  this._allUnsavedComponents$.next([]);
                  finished$.next();
                }
                return of(true);
              case 'save':
                if (applyAllChecked) {
                  const remainingComponents = components.slice(currentIndex);
                  const {saveSupported, saveNotSupported} = this.splitSaveImplementingComponents(remainingComponents);
                  return this.trySaveAll(saveSupported).pipe(
                    tap(stillUnsavedComponents => {
                      const unsavedComponents = [...saveNotSupported, ...stillUnsavedComponents];
                      this._allUnsavedComponents$.next(unsavedComponents);
                      if (unsavedComponents.length === 0) {
                        finished$.next();
                      } 
                    })
                  );
                } else {
                  return component.unsavedChangesConfiguration.saveAction()
                }
              default:
                throw new Error(`Invalid button response: ${ buttonClicked }`)
            }
          }),
          take(1)
        )
      }),
      takeUntil(finished$),
      toArray(),
      map((responses) => !responses.some(r => r === false)),
    )
  }

  pushDialogResponse(response: DialogResponse) {
    this._dialogResponses$.next(response);
  }


  private splitSaveImplementingComponents(components: AvanoUnsavedChangesSupport[]) {
    const saveNotSupported: AvanoUnsavedChangesSupport[] = [];
    const saveSupported: AvanoUnsavedChangesSupport[] = [];
    components.forEach(remainingComponent => {
      const canSave = typeof remainingComponent.unsavedChangesConfiguration.canSave === 'function' && remainingComponent.unsavedChangesConfiguration.canSave();
      if (canSave) {
        saveSupported.push(remainingComponent);
      } else {
        saveNotSupported.push(remainingComponent);
      }
    });
    return {
      saveSupported,
      saveNotSupported
    }
  }

  private trySaveAll(componentsToSave: AvanoUnsavedChangesSupport[]) {
    return from(componentsToSave).pipe(
      mergeMap(comp => zip(of(comp), comp.unsavedChangesConfiguration.saveAction().pipe(catchError(() => of(false))))),
      toArray(),
      map((responses) => (responses.filter(([_, saveResult]) => saveResult === false).map(([component])=>component))),
    );
  }
}
